import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import ServiceSelectionModal from 'components/modals/ServiceSelectionModal';
import AssignmentSubmissionForm from 'components/modals/AssignmentSubmissionForm';
import ClassManagementForm from 'components/modals/ClassManagementForm';
import ReferralCodeGenerator from 'components/modals/ReferralCodeGenerator';
import { motion, AnimatePresence } from 'framer-motion';
import WhatsAppIcon from 'components/icons/WhatsAppIcon';
import GradientIcon from 'components/ui/GradientIcon';

// Legacy country codes and PhoneInput component removed

const SolutionOverview = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  
  // Solutions data - needs to be defined before state that uses it
  const solutions = {
    assignments: {
      title: "Assignment Help",
      subtitle: "Professional assignment completion with guaranteed quality",
      icon: "FileText",
      gradient: "primary",
      features: [
        "Expert writers with advanced degrees",
        "Original, plagiarism-free content",
        "24/7 progress tracking",
        "Unlimited revisions included",
        "On-time delivery guarantee"
      ],
      pricing: "Starting at $8/page",
      deliveryTime: "24-72 hours",
      description: `Get professional help with essays, research papers, case studies, and more. Our expert writers handle the workload while you focus on learning and other priorities. Every assignment is custom-written, thoroughly researched, and delivered on time with quality guarantees.`,
      benefits: [
        { icon: "Clock", text: "Save 15+ hours per week" },
        { icon: "TrendingUp", text: "Improve GPA by 0.8 points average" },
        { icon: "Shield", text: "100% confidential service" }
      ]
    },
    classes: {
      title: "Class Management",
      subtitle: "Complete course management and attendance support",
      icon: "BookOpen",
      gradient: "ocean",
      features: [
        "Virtual class attendance",
        "Note-taking and summaries",
        "Discussion board participation",
        "Quiz and exam preparation",
        "Progress monitoring"
      ],
      pricing: "Starting at $199/class",
      deliveryTime: "Ongoing support",
      description: `Let us manage your entire course while you focus on what matters most. From attending virtual classes to participating in discussions and completing coursework, we handle everything while keeping you informed of your progress.`,
      benefits: [
        { icon: "Users", text: "Professional class representation" },
        { icon: "Target", text: "Maintain consistent attendance" },
        { icon: "BarChart", text: "Real-time progress updates" }
      ]
    },
    referrals: {
      title: "Refer a Friend",
      subtitle: "Invite a Friend, Save on Your Next Assignment",
      icon: "Gift",
      gradient: "sunset",
      features: [
        "10% OFF for your friend",
        "15% OFF your next assignment",
        "Stack rewards with multiple referrals",
        "Priority support access",
        "Priority assignment support"
      ],
      pricing: "Earn up to $500/month",
      deliveryTime: "Instant rewards",
      description: `Share SOMO with your friends and get rewarded! Simply request a referral code by filling out the form, and we'll send your personal code to you. Once your friends use your code, both of you will receive special discounts on your next orders.`,
      benefits: [
        { icon: "HandCoins", text: "10% OFF for your friend" },
        { icon: "BadgePercent", text: "15% OFF your next assignment" },
        { icon: "Crown", text: "VIP status for your next order" },
        { icon: "Zap", text: "Priority assignment support" },
      ]
    }
  };

  const [activeTab, setActiveTab] = useState('assignments');
  // Removed legacy referral form state
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [showClassManagementForm, setShowClassManagementForm] = useState(false);
  const [showReferralCodeGenerator, setShowReferralCodeGenerator] = useState(false);
  
  // Touch/swipe functionality
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const contentRef = useRef(null);
  
  // Auto-advance functionality for mobile
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const autoAdvanceTimer = useRef(null);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  
  // Get tab keys for easier navigation
  const tabKeys = Object.keys(solutions);
  const currentTabIndex = tabKeys.indexOf(activeTab);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Swipe detection
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentTabIndex < tabKeys.length - 1) {
      // Swipe left - next tab
      setSwipeDirection('left');
      setTimeout(() => {
        setActiveTab(tabKeys[currentTabIndex + 1]);
        setSwipeDirection(null);
        setShowSwipeHint(false);
      }, 100);
    } else if (isRightSwipe && currentTabIndex > 0) {
      // Swipe right - previous tab
      setSwipeDirection('right');
      setTimeout(() => {
        setActiveTab(tabKeys[currentTabIndex - 1]);
        setSwipeDirection(null);
        setShowSwipeHint(false);
      }, 100);
    }
  };

  // Auto-advance functionality (disabled for better UX)
  const clearAutoAdvanceTimer = () => {
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }
  };

  // Hide swipe hints after user interaction
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    
    if (isMobile && showSwipeHint) {
      // Show swipe hints for longer since no auto-advance
      const hintTimer = setTimeout(() => {
        setShowSwipeHint(false);
      }, 8000);
      return () => clearTimeout(hintTimer);
    }
  }, [showSwipeHint]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearAutoAdvanceTimer();
  }, []);

  // Legacy referral form submission function removed

  // Handle service selection from the modal
  const handleServiceSelection = (serviceId) => {
    setShowServiceModal(false);

    // Show the appropriate form based on the selected service
    if (serviceId === 'assignment') {
      setShowAssignmentForm(true);
    } else if (serviceId === 'class-management') {
      setShowClassManagementForm(true);
    } else if (serviceId === 'consultation') {
      // Handle consultation request (e.g., open WhatsApp)
      const phoneNumber = '254793863320';
      const message = encodeURIComponent('Hi! I would like to request a consultation about your academic services. Can we discuss my specific needs?');
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    }
  };

  // Handle button clicks with authentication check
  const handleButtonClick = (action) => {
    if (!isLoggedIn) {
      // Redirect to login page
      navigate('/login');
      return;
    }

    // User is logged in, perform the action
    switch (action) {
      case 'assignment':
        setShowAssignmentForm(true);
        break;
      case 'class-management':
        setShowClassManagementForm(true);
        break;
      case 'referral':
        setShowReferralCodeGenerator(true);
        break;
      default:
        setShowServiceModal(true);
    }
  };

  const currentSolution = solutions[activeTab];

  return (
    <section id="solutions" className="solution-overview py-20 bg-background">
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-md glass-effect-light ${
          notification.type === 'success' ? 'border border-green-400 text-green-400' : 
          notification.type === 'error' ? 'border border-red-400 text-red-400' : 
          'border border-blue-400 text-blue-400'
        }`}>
          <div className="flex items-center">
            <Icon 
              name={notification.type === 'success' ? 'CheckCircle' : notification.type === 'error' ? 'AlertCircle' : 'Info'} 
              size={20} 
              className="mr-2" 
            />
            <p>{notification.message}</p>
            <button 
              onClick={() => setNotification({...notification, show: false})}
              className="ml-auto text-gray-500 hover:text-gray-700"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <ServiceSelectionModal 
        isOpen={showServiceModal} 
        onClose={() => setShowServiceModal(false)} 
        onSelectService={handleServiceSelection} 
      />

      <AssignmentSubmissionForm 
        isOpen={showAssignmentForm} 
        onClose={() => setShowAssignmentForm(false)} 
        onSubmit={() => {
          // Handle successful submission
          setNotification({
            show: true,
            message: 'Your assignment has been submitted successfully! Our team will contact you shortly.',
            type: 'success'
          });
        }} 
      />

      <ClassManagementForm 
        isOpen={showClassManagementForm} 
        onClose={() => setShowClassManagementForm(false)} 
        onSubmit={() => {
          // Handle successful submission
          setNotification({
            show: true,
            message: 'Your class management request has been submitted successfully! Our team will contact you shortly.',
            type: 'success'
          });
        }} 
      />

      <ReferralCodeGenerator
        isOpen={showReferralCodeGenerator}
        onClose={() => setShowReferralCodeGenerator(false)}
      />

      {/* Legacy Referral Form Modal removed */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-text-primary mb-6">
            <span className="text-gradient">What we do </span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Choose the support level that fits your needs. From individual assignments to complete course management, we've got you covered.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-col justify-center mb-12">
          {/* Desktop tab navigation */}
          <div className="hidden lg:flex justify-center">
            <div className="glass-card rounded-xl p-2 inline-flex space-x-2 relative">
              {/* Scroll indicator hint */}
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-sm text-text-muted flex items-center space-x-1">
                <Icon name="ArrowLeft" size={16} className="animate-pulse" />
                <span>Click to explore</span>
                <Icon name="ArrowRight" size={16} className="animate-pulse" />
              </div>
              
              {Object.entries(solutions).map(([key, solution]) => (
                <button
                  key={key}
                  onClick={() => {
                    setActiveTab(key);
                  }}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 relative ${
                    activeTab === key
                      ? 'bg-primary text-white shadow-lg transform scale-105'
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface-light hover:scale-102'
                  }`}
                >
                  <GradientIcon name={solution.icon} size={20} gradient={solution.gradient} containerClassName="w-6 h-6" />
                  <span>{solution.title}</span>
                  
                  {/* Active indicator */}
                  {activeTab === key && (
                    <motion.div
                      className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-white rounded-full"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile tab indicators (dots) with swipe hint */}
          <div className="lg:hidden">
            {/* Swipe instruction */}
            <div className="text-center mb-4">
              <div className="text-sm text-text-muted flex items-center justify-center space-x-2">
                <Icon name="ArrowLeft" size={16} className="animate-pulse" />
                <span>Swipe to explore solutions</span>
                <Icon name="ArrowRight" size={16} className="animate-pulse" />
              </div>
            </div>
            
            {/* Tab indicators */}
            <div className="flex justify-center space-x-3 mb-4">
              {tabKeys.map((key, index) => (
                <button
                  key={key}
                  onClick={() => {
                    setActiveTab(key);
                    setShowSwipeHint(false);
                  }}
                  className={`relative transition-all duration-300 ${
                    index === currentTabIndex 
                      ? 'w-8 h-3 bg-primary rounded-full' 
                      : 'w-3 h-3 bg-gray-300 hover:bg-gray-400 rounded-full'
                  }`}
                >
                  {/* Active indicator enhancement */}
                  {index === currentTabIndex && (
                    <motion.div
                      className="absolute inset-0 bg-primary/30 rounded-full"
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ 
                        duration: 2, 
                        repeat: Infinity, 
                        ease: "easeInOut" 
                      }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile tab titles */}
          <div className="lg:hidden text-center">
            <h3 className="text-xl font-semibold text-text-primary flex items-center justify-center space-x-2">
              <GradientIcon name={currentSolution.icon} size={20} gradient={currentSolution.gradient} containerClassName="w-6 h-6" />
              <span>{currentSolution.title}</span>
            </h3>
          </div>
        </div>

        {/* Active Solution Content */}
        <div className="glass-card rounded-2xl overflow-hidden relative">
          {/* Swipe hint arrows for mobile */}
          {showSwipeHint && (
            <div className="absolute top-1/2 left-4 right-4 flex justify-between z-10 lg:hidden pointer-events-none">
              <motion.div
                animate={{ x: [-5, 5, -5] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="bg-white/90 rounded-full p-1 shadow-lg"
              >
                <Icon name="ChevronLeft" size={24} className="text-primary" />
              </motion.div>
              <motion.div
                animate={{ x: [5, -5, 5] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="bg-white/90 rounded-full p-1 shadow-lg"
              >
                <Icon name="ChevronRight" size={24} className="text-primary" />
              </motion.div>
            </div>
          )}
          
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-0"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            animate={swipeDirection ? { x: swipeDirection === 'left' ? -10 : 10 } : { x: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Left Side - Content */}
            <div className="p-8 lg:p-12">
              <div className="flex items-center space-x-3 mb-6">
                <GradientIcon 
                  name={currentSolution.icon} 
                  size={32} 
                  gradient={currentSolution.gradient}
                  containerClassName="w-16 h-16 rounded-xl"
                />
                <div>
                  <h3 className="text-3xl font-bold text-text-primary">{currentSolution.title}</h3>
                  <p className="text-text-secondary">{currentSolution.subtitle}</p>
                </div>
              </div>

              <p className="text-text-secondary mb-8 leading-relaxed">
                {currentSolution.description}
              </p>

              {/* Features List - Only show for non-referrals tabs */}
              {activeTab !== 'referrals' && (
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-text-primary mb-4">What's Included:</h4>
                  <div className="space-y-3">
                    {currentSolution.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-text-primary">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing and Delivery - Only show for non-referrals tabs */}
              {activeTab !== 'referrals' && (
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="glass-card rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="DollarSign" size={16} className="text-success" />
                      <span className="text-text-muted text-sm">Pricing</span>
                    </div>
                    <div className="text-xl font-bold text-text-primary">{currentSolution.pricing}</div>
                  </div>
                  <div className="glass-card rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Clock" size={16} className="text-primary" />
                      <span className="text-text-muted text-sm">Delivery</span>
                    </div>
                    <div className="text-xl font-bold text-text-primary">{currentSolution.deliveryTime}</div>
                  </div>
                </div>
              )}

              {/* CTA Button */}
              <button
                onClick={() => {
                  if (activeTab === 'referrals') {
                    // Open the referral code generator (requires login)
                    handleButtonClick('referral');
                  } else if (activeTab === 'assignments') {
                    // Open the assignment submission form (requires login)
                    handleButtonClick('assignment');
                  } else if (activeTab === 'classes') {
                    // Open the class management form (requires login)
                    handleButtonClick('class-management');
                  }
                }}
                className="cta-button w-full btn-primary text-center py-4 text-lg font-bold"
              >
                {activeTab === 'referrals' ? 'Request Referral Code' : `Get Started with ${currentSolution.title}`}
              </button>
            </div>

            {/* Right Side - Benefits */}
            <div className="glass-effect p-8 lg:p-12 backdrop-blur-md bg-white/5">
              <h4 className="text-2xl font-bold text-text-primary mb-8">Key Benefits</h4>

              <div className="space-y-6">
                {currentSolution.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <GradientIcon 
                      name={benefit.icon} 
                      size={24} 
                      gradient={currentSolution.gradient}
                      containerClassName="w-12 h-12 rounded-lg flex-shrink-0"
                    />
                    <div>
                      <p className="text-text-primary font-semibold text-lg">{benefit.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Success Metrics - Only show for non-referrals tabs */}
              {activeTab !== 'referrals' && (
                <div className="mt-12 p-6 glass-card rounded-xl">
                  <h5 className="text-lg font-semibold text-text-primary mb-4">Success Metrics</h5>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-accent">98%</div>
                      <div className="text-text-muted text-sm">Satisfaction Rate</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-accent">4.9/5</div>
                      <div className="text-text-muted text-sm">Average Rating</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Guarantee Badge */}
              <div className="mt-8 text-center">
                <div className="inline-flex items-center space-x-2 text-accent px-4 py-2 rounded-full glass-card">
                  <Icon name="Shield" size={16} className="text-success" />
                  <span className="font-semibold text-sm">Money-Back Guarantee</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-text-secondary mb-6">
            Not sure which option is right for you? Let's discuss your specific needs.
          </p>
          <motion.button
              onClick={() => {
                const message = encodeURIComponent("Hi! I'd like to discuss which academic support option would be best for my situation. Can we schedule a consultation?");
                window.open(`https://wa.me/254793863320?text=${message}`, '_blank');
              }}
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 10px 25px rgba(37, 211, 102, 0.3)"
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { duration: 0.1 }
              }}
              className="relative inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#128C7E] hover:to-[#25D366] text-white rounded-xl shadow-lg font-bold transition-all duration-300 overflow-hidden group"
          >
            <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out"></div>
            <WhatsAppIcon size={20} className="relative z-10 text-white flex-shrink-0"/>
            <span className="relative z-10 font-semibold">Get Free Consultation</span>
            <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
            </div>
          </motion.button>
        </div>
      </div>
    </section>
  );
};

/* Legacy Referral Form Modal removed */

export default SolutionOverview;
